import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQuery} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import {AlertIcon, BookIcon, type Icon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type ReactNode, useCallback, useMemo} from 'react'

import {savedViewsQueryOptions} from '../../queries/saved-views'
import styles from './RepositoryViews.module.css'
import {DEFAULT_VIEWS_SORT, isViewsSortValue, sortViews, type ViewsSortValue} from './views-sort-options'
import {ViewsList} from './ViewsList'

function ViewsBlankslate({
  icon: IconComponent,
  heading,
  children,
  primaryAction,
}: {
  icon: Icon
  heading: string
  children: ReactNode
  primaryAction?: {href: string; label: string}
}) {
  return (
    <Blankslate spacious>
      <Blankslate.Visual>
        <IconComponent size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{heading}</Blankslate.Heading>
      <Blankslate.Description>{children}</Blankslate.Description>
      {primaryAction ? (
        <Blankslate.PrimaryAction href={primaryAction.href}>{primaryAction.label}</Blankslate.PrimaryAction>
      ) : null}
    </Blankslate>
  )
}

export function ViewsIndexContent() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const sortParam = searchParams.get('sort')
  const sort = isViewsSortValue(sortParam) ? sortParam : DEFAULT_VIEWS_SORT

  const handleSortChange = useCallback(
    (next: ViewsSortValue) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (next === DEFAULT_VIEWS_SORT) {
            params.delete('sort')
          } else {
            params.set('sort', next)
          }
          return params
        },
        {replace: true, preventAutofocus: true},
      )
    },
    [setSearchParams],
  )

  const newViewUrl = `/${owner}/${repo}/issues/views/new`

  const {data, isError} = useQuery(savedViewsQueryOptions({owner, repo}))
  const sortedViews = useMemo(() => sortViews(data?.savedViews ?? [], sort), [data?.savedViews, sort])
  const isLoading = !isError && data === undefined
  const isEmpty = !isError && !isLoading && sortedViews.length === 0

  return (
    <div className={styles.viewsPage}>
      <div className={styles.headerRow}>
        <Heading as="h1" className={styles.heading}>
          Views
        </Heading>
        {!isEmpty && (
          <Button as="a" href={newViewUrl} variant="primary">
            New view
          </Button>
        )}
      </div>
      <div className={styles.viewsListWrapper} data-hpc aria-busy={isLoading}>
        <ErrorBoundary
          boundaryName="RepositoryViewsIndexPage"
          fallback={
            <ViewsBlankslate icon={AlertIcon} heading="Something went wrong">
              We couldn’t load your views. Please try again.
            </ViewsBlankslate>
          }
        >
          {isError ? (
            <ViewsBlankslate icon={AlertIcon} heading="Something went wrong">
              We couldn’t load your views. Please try again.
            </ViewsBlankslate>
          ) : isEmpty ? (
            <ViewsBlankslate
              icon={BookIcon}
              heading="No views yet"
              primaryAction={{href: newViewUrl, label: 'New view'}}
            >
              Create views for custom queries and filters.
            </ViewsBlankslate>
          ) : (
            <ViewsList
              views={sortedViews}
              owner={owner}
              repo={repo}
              sort={sort}
              onSortChange={handleSortChange}
              isLoading={isLoading}
            />
          )}
        </ErrorBoundary>
      </div>
    </div>
  )
}
